"use client"

import { cn } from "../lib/utils"
import React, { useCallback, useEffect, useState, useMemo } from "react"
import { AnimatedMarkdown } from 'flowtoken'
import 'flowtoken/dist/styles.css'

export type Mode = "typewriter" | "fade" | "dropIn" | "slideUp" | "blurIn"

export type UseTextStreamOptions = {
  textStream: string | AsyncIterable<string>
  speed?: number
  mode?: Mode
  onComplete?: () => void
  onError?: (error: unknown) => void
}

export type UseTextStreamResult = {
  streamingContent: string
  isComplete: boolean
  reset: () => void
  startStreaming: () => void
}

function useTextStream({
  textStream,
  speed = 20,
  mode = "typewriter",
  onComplete,
  onError,
}: UseTextStreamOptions): UseTextStreamResult {
  const [streamingContent, setStreamingContent] = useState("")
  const [isComplete, setIsComplete] = useState(false)

  const processStream = useCallback(async () => {
    setStreamingContent("")
    setIsComplete(false)

    try {
      let fullText = ""
      
      // First, get the complete text
      if (typeof textStream === "string") {
        fullText = textStream
      } else if (textStream) {
        for await (const chunk of textStream) {
          fullText += chunk
        }
      }

      // Now stream it character by character or word by word
      if (mode === "typewriter") {
        // Character by character for typewriter effect
        for (let i = 0; i <= fullText.length; i++) {
          setStreamingContent(fullText.slice(0, i))
          const delay = Math.max(20, 200 - (speed * 2)) // 20ms to 200ms per character
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      } else {
        // Word by word for other animations
        const words = fullText.split(' ')
        let currentContent = ""
        
        for (let i = 0; i < words.length; i++) {
          currentContent += (i > 0 ? ' ' : '') + words[i]
          setStreamingContent(currentContent)
          const delay = Math.max(100, 800 - (speed * 7)) // 100ms to 800ms per word
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
      
      setIsComplete(true)
      onComplete?.()
    } catch (error) {
      console.error("Error processing text stream:", error)
      onError?.(error)
      setIsComplete(true)
    }
  }, [textStream, speed, mode, onComplete, onError])

  const reset = useCallback(() => {
    setStreamingContent("")
    setIsComplete(false)
  }, [])

  const startStreaming = useCallback(() => {
    processStream()
  }, [processStream])

  useEffect(() => {
    startStreaming()
  }, [startStreaming])

  return {
    streamingContent,
    isComplete,
    reset,
    startStreaming,
  }
}

export type ResponseStreamProps = {
  textStream: string | AsyncIterable<string>
  mode?: Mode
  speed?: number // 1-100, where 1 is slowest and 100 is fastest
  className?: string
  onComplete?: () => void
  as?: keyof React.JSX.IntrinsicElements
  renderContent?: (text: string) => React.ReactNode
  fadeDuration?: number // Custom fade duration in ms (overrides speed)
  segmentDelay?: number // For backward compatibility
  characterChunkSize?: number // For backward compatibility
}

function ResponseStream({
  textStream,
  mode = "fade",
  speed = 30,
  className = "",
  onComplete,
  as = "div",
  renderContent,
  fadeDuration,
}: ResponseStreamProps) {
  const [contentKey, setContentKey] = useState(0)
  
  const { streamingContent, isComplete } = useTextStream({
    textStream,
    speed,
    mode,
    onComplete,
  })

  // Reset when textStream changes
  useEffect(() => {
    setContentKey(prev => prev + 1)
  }, [textStream])

  // Animation duration based on speed - much faster for streaming
  const animationDuration = useMemo(() => {
    if (fadeDuration) {
      return `${fadeDuration}ms`
    }
    
    // Fast animations for smooth streaming effect
    if (mode === "typewriter") {
      return "0.1s" // Very fast for typewriter
    }
    
    const duration = Math.max(0.2, 0.6 - (speed / 200)) // 0.2s to 0.6s
    return `${duration}s`
  }, [speed, fadeDuration, mode])

  // Get flowtoken animation type
  const flowTokenAnimation = useMemo(() => {
    switch (mode) {
      case "typewriter":
        return "typewriter"
      case "fade":
        return "fadeIn"
      case "dropIn":
        return "dropIn"
      case "slideUp":
        return "slideUp"
      case "blurIn":
        return "blurIn"
      default:
        return "fadeIn"
    }
  }, [mode])

  // Custom content renderer
  const content = renderContent ? renderContent(streamingContent) : streamingContent

  // If we have a custom renderer that returns JSX, render it normally
  if (renderContent && React.isValidElement(content)) {
    return React.createElement(as, { className: cn(className) }, content)
  }

  // Use a div wrapper to avoid JSX children prop conflicts
  if (as === "div") {
    return (
      <div className={cn(className)}>
        <AnimatedMarkdown
          key={contentKey}
          content={String(content)}
          animation={flowTokenAnimation}
          animationDuration={animationDuration}
          animationTimingFunction="ease-in-out"
          sep={mode === "typewriter" ? "char" : "word"}
        />
      </div>
    )
  }

  // For other elements, create them dynamically
  return React.createElement(
    as,
    { className: cn(className) },
    React.createElement(AnimatedMarkdown, {
      key: contentKey,
      content: String(content),
      animation: flowTokenAnimation,
      animationDuration: animationDuration,
      animationTimingFunction: "ease-in-out",
      sep: mode === "typewriter" ? "char" : "word"
    })
  )
}

export { useTextStream, ResponseStream }
